import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { NavLink, useLocation } from "react-router";

import { isActive, NAV, type NavItem } from "./navigation";

/** 叶子项：一个能点进去的页面。`depth` 只影响缩进。 */
function Leaf({ item, depth, onNavigate }: { item: NavItem; depth: number; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  return (
    <ListItemButton
      component={NavLink}
      to={item.to}
      selected={isActive(item, pathname)}
      onClick={onNavigate}
      sx={{ mb: 0.25, pl: depth ? 2 + depth * 1.5 : undefined }}
    >
      <ListItemIcon>{item.icon}</ListItemIcon>
      <ListItemText primary={item.label} />
    </ListItemButton>
  );
}

/** 已登记但还没开放的一支：看得见、点不进去、说明为什么。 */
function Pending({ item }: { item: NavItem }) {
  return (
    <Tooltip title={item.note ?? ""} placement="right">
      {/* disabled 的按钮不发鼠标事件，Tooltip 要挂在外面这层 */}
      <Box sx={{ mb: 0.25 }}>
        <ListItemButton disabled sx={{ width: "100%" }}>
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText
            primary={item.label}
            secondary={
              <Typography component="span" variant="caption" sx={{ color: "text.disabled" }}>
                未开放
              </Typography>
            }
            sx={{ display: "flex", alignItems: "baseline", gap: 0.75, my: 0 }}
            slotProps={{ secondary: { sx: { mt: 0 } } }}
          />
        </ListItemButton>
      </Box>
    </Tooltip>
  );
}

/**
 * 一支子菜单（账号的类别、网关的种类）：父项只管展开，页面都在子项里。
 *
 * 父项不做跳转：它不是一个页面，点一下直接跳进第一个子项会让"展开看看有什么"变成换页。
 * 当前页在这一支里时默认展开，人手动折叠过就听人的。
 */
function Branch({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const active = isActive(item, pathname);
  const [collapsed, setCollapsed] = useState(false);
  const open = active || !collapsed;
  return (
    <>
      <ListItemButton onClick={() => setCollapsed(open)} sx={{ mb: 0.25 }}>
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={item.label} slotProps={{ primary: { sx: { fontWeight: active ? 650 : 500 } } }} />
        <ExpandMoreRounded
          sx={{
            fontSize: 16,
            color: "text.disabled",
            transition: "transform .15s",
            transform: open ? "none" : "rotate(-90deg)",
          }}
        />
      </ListItemButton>
      <Collapse in={open} unmountOnExit>
        <List dense disablePadding>
          {item.children?.map((child) => <Leaf key={child.to} item={child} depth={1} onNavigate={onNavigate} />)}
        </List>
      </Collapse>
    </>
  );
}

export function MenuContent({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <List dense disablePadding sx={{ "& + &": { mt: 0.5 } }}>
      {NAV.map((group, gi) => (
        <List
          key={group.title ?? gi}
          dense
          disablePadding
          subheader={group.title ? <ListSubheader disableSticky>{group.title}</ListSubheader> : undefined}
          sx={{ mb: 0.25 }}
        >
          {group.items.map((item) => {
            if (item.disabled) return <Pending key={item.to} item={item} />;
            if (item.children) return <Branch key={item.to} item={item} onNavigate={onNavigate} />;
            return <Leaf key={item.to} item={item} depth={0} onNavigate={onNavigate} />;
          })}
        </List>
      ))}
    </List>
  );
}
